import {
  Box,
  Button,
  CloseButton,
  Drawer,
  Flex,
  IconButton,
  Portal,
  Separator,
  Stack,
  Text,
} from '@chakra-ui/react';
import { memo, useState } from 'react';
import { CiMenuFries } from 'react-icons/ci';
import { LuInfo } from 'react-icons/lu';

import UserAvatarMenu from '@components/common/UserAvatarMenu';
import { ColorModeButton } from '@components/ui/color-mode';
import { usePageFormControlsContext } from '@features/navigation/context';
import NavigationTitle from '@features/navigation/NavigationTitle';
import { PageNavigation } from '@features/navigation/PageNavigation';
import SearchBar from '@features/navigation/SearchBar';
import { NAVIGATION_TOP_BAR_HEIGHT_PX } from 'config/layout/navigationPanel';
import { toPxString } from 'helpers/styles';
import { useOrgSlug } from 'hooks/useOrgSlug';

interface FormActionBarProps {
  onDiscard: () => void;
  onSave: () => void;
}
const FormActionBar = ({ onDiscard, onSave }: FormActionBarProps) => {
  return (
    <Flex
      gap={2}
      alignItems="center"
      border="1px solid"
      borderColor="border.light"
      bg="sidebar.background"
      px={2}
      py={1}
      borderRadius="md"
      w={{ base: 'md', lgDown: 'full' }}
    >
      <LuInfo />
      <Text userSelect="none" flex={1} fontSize="xs">
        Unsaved changes
      </Text>
      <Button
        size="2xs"
        variant="outline"
        borderColor="border.light"
        _hover={{ bg: 'sidebar.hoverButton' }}
        onClick={onDiscard}
      >
        Discard
      </Button>
      <Button size="2xs" onClick={onSave}>
        Save
      </Button>
    </Flex>
  );
};

const SidebarMenuButton = () => {
  const [open, setOpen] = useState(false);

  const onToggle = () => {
    setOpen(!open);
  };

  return (
    <Drawer.Root size="full" onOpenChange={onToggle} open={open}>
      <Drawer.Trigger asChild>
        <IconButton variant="ghost" _hover={{ bg: 'sidebar.hoverButton' }} hideFrom="lg">
          <CiMenuFries />
        </IconButton>
      </Drawer.Trigger>
      <Portal>
        <Drawer.Backdrop />
        <Drawer.Positioner>
          <Drawer.Content>
            <Drawer.Header>
              <Drawer.CloseTrigger asChild pos="initial">
                <CloseButton />
              </Drawer.CloseTrigger>
            </Drawer.Header>
            <Drawer.Body>
              <PageNavigation onSelect={onToggle} />
            </Drawer.Body>
          </Drawer.Content>
        </Drawer.Positioner>
      </Portal>
    </Drawer.Root>
  );
};

const TopNavigationBar = () => {
  const [context] = usePageFormControlsContext();
  const orgSlug = useOrgSlug();
  const shouldRedirectToLogin = orgSlug != null;

  return (
    <Stack
      position="relative"
      gap={0}
      h={toPxString(NAVIGATION_TOP_BAR_HEIGHT_PX)}
      justifyContent="center"
    >
      <Flex alignItems="center" gap={2} pt={1}>
        {/* Left zone: shrinks and truncates so a long title never overlaps the
            centered search bar (flex basis 0 keeps the center truly centered). */}
        <Flex alignItems="center" gap={2} pl={3} flex="1 1 0" minW={0}>
          <SidebarMenuButton />
          <NavigationTitle />
        </Flex>
        <Box hideBelow="xl" flexShrink={0}>
          {context.showFormControls ? (
            <FormActionBar onDiscard={context.onDiscard} onSave={context.onSave} />
          ) : (
            <SearchBar width="md" />
          )}
        </Box>
        {/* Only mirror the left zone's flex at xl+, where the centered search bar
            needs equal side zones. Below xl (search hidden) the right zone is
            content-sized so the title gets the full remaining width. */}
        <Flex
          flexDirection="row"
          gap={2}
          justifyContent="flex-end"
          pr={3}
          flex={{ base: '0 0 auto', xl: '1 1 0' }}
          minW={0}
        >
          <ColorModeButton />
          <UserAvatarMenu
            showOrgList={orgSlug == null}
            enableCreateBusiness={orgSlug == null}
            shouldRedirectToLogin={shouldRedirectToLogin}
          />
        </Flex>
      </Flex>
      {context.showFormControls && (
        <Flex hideFrom="lg" w="full" justifyContent="center">
          <FormActionBar onDiscard={context.onDiscard} onSave={context.onSave} />
        </Flex>
      )}
      <Separator borderColor="border.light" mt={1} />
    </Stack>
  );
};

export default memo(TopNavigationBar);
